/**
 * The gateway strategy's settings — the Config tab of the Gateway strategy
 * pop-up, in the step rows of Schedule (icon, label, ⓘ, the choice): the
 * mode cards, then the row the choice needs: rotation order for
 * Hold/Alternate, weights for Random/Split — each by hand or by the gateway
 * formula (Phase 17), the rules' outcome (Phase 19) under it.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Shuffle } from 'lucide-react';
import type { DropGenGatewayRankingPreview } from '@trafficpulse/shared';

import type { DraftGateway } from '../../../drop-generator.types';
import { StepRow } from '../general/controls/StepRow';
import type { GatewayOp } from '../state/gateway-draft';
import { STRATEGY_HINT } from './cards/strategy-copy';
import { StrategyCards } from './cards/StrategyCards';
import { poolShares, rankingNeeded, rotationOrder } from './ranking/formula-pool';
import { OrderZone } from './zones/OrderZone';
import { WeightsZone } from './zones/WeightsZone';

interface Props {
  gateway: DraftGateway;
  /** Enabled, unpinned slots — what SPLIT divides. */
  splitOver: number;
  ranking: DropGenGatewayRankingPreview | undefined;
  onOp: (op: GatewayOp) => void;
}

export const StrategyConfig: FC<Props> = ({ gateway, splitOver, ranking, onOp }) => {
  const weighted = gateway.strategy === 'RANDOM' || gateway.strategy === 'SPLIT';
  const preview = rankingNeeded(gateway) ? ranking : undefined;
  const view = rotationOrder(gateway, preview);
  return (
    <div className="divide-y divide-border/50">
      <StepRow icon={Shuffle} label="Strategy" hint={STRATEGY_HINT}>
        <StrategyCards strategy={gateway.strategy} onSelect={(strategy) => onOp({ kind: 'patch', patch: { strategy } })} />
      </StepRow>
      {weighted ? (
        <WeightsZone gateway={gateway} shares={poolShares(gateway, preview)} view={view} splitOver={splitOver} onOp={onOp} />
      ) : (
        <OrderZone gateway={gateway} view={view} ranking={gateway.orderMode === 'FORMULA' && preview !== undefined} onOp={onOp} />
      )}
    </div>
  );
};
